import React, { useState, useEffect } from 'react';
import { Card, CardBody, Typography, Input, Button } from "@material-tailwind/react";
import axios from 'axios';
import { FaTrash, FaPlus, FaTimes, FaPenAlt, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import { useAuth } from '../myTrack/AuthContext';
import { rootUrl } from '../../config/config';

const JobCardStagesManagement = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({
        code: '',
        name: '',
        color: '#3B82F6',
        sort_order: 0,
        is_active: true,
        invoice: false,
        to_invoice: false,
        locked: false,
        cancel: false,
        needs_amount: false,
    });
    const { user } = useAuth();

    const token = localStorage.getItem('authToken') || '';

    const fetchItems = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${rootUrl}/api/system/job-card-stages`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setItems(response.data.data || []);
            setError('');
        } catch (err) {
            setError('Failed to fetch job card stages.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchItems();
    }, []);

    const startEdit = (item) => {
        setEditingId(item.id);
        setForm({
            code: item.code || '',
            name: item.name || '',
            color: item.color || '#3B82F6',
            sort_order: item.sort_order || 0,
            is_active: !!item.is_active,
            invoice: !!item.invoice,
            to_invoice: !!item.to_invoice,
            locked: !!item.locked,
            cancel: !!item.cancel,
            needs_amount: !!item.needs_amount,
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const cancelForm = () => {
        setEditingId(null);
        setForm({ code: '', name: '', color: '#3B82F6', sort_order: 0, is_active: true, invoice: false, to_invoice: false, locked: false, cancel: false, needs_amount: false });
        setError('');
    };

    const deleteItem = async (id) => {
        if (!window.confirm('Delete this job card stage? This will only work if no job cards are using this stage.')) return;
        try {
            const response = await axios.delete(`${rootUrl}/api/system/job-card-stages/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data.success) {
                fetchItems();
            } else {
                setError(response.data.message || 'Failed to delete stage.');
            }
        } catch (err) {
            const msg = err?.response?.data?.message || 'Failed to delete stage.';
            setError(msg);
            console.error(err);
        }
    };

    const toggleActive = async (item) => {
        try {
            await axios.patch(
                `${rootUrl}/api/system/job-card-stages/${item.id}/toggle`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            fetchItems();
        } catch (err) {
            setError('Failed to toggle stage status.');
            console.error(err);
        }
    };

    const validateForm = () => {
        const code = form.code.trim();
        const name = form.name.trim();
        if (!code || !name) return 'Code and name are required.';
        if (!/^#[0-9A-F]{6}$/i.test(form.color)) return 'Valid hex color is required (e.g., #3B82F6).';
        return '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationError = validateForm();
        if (validationError) { setError(validationError); return; }

        try {
            const payload = {
                code: form.code.toUpperCase(),
                name: form.name,
                color: form.color.toUpperCase(),
                sort_order: parseInt(form.sort_order) || 0,
                invoice: form.invoice,
                to_invoice: form.to_invoice,
                locked: form.locked,
                cancel: form.cancel,
                needs_amount: form.needs_amount,
                ...(editingId ? { edit_by: user?.username || 'system' } : { input_by: user?.username || 'system' }),
            };

            if (editingId) {
                await axios.put(`${rootUrl}/api/system/job-card-stages/${editingId}`, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            } else {
                await axios.post(`${rootUrl}/api/system/job-card-stages`, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            }

            cancelForm();
            fetchItems();
        } catch (err) {
            const msg = err?.response?.data?.message || (editingId ? 'Failed to update stage.' : 'Failed to add stage.');
            setError(msg);
            console.error(err);
        }
    };

    return (
        <div className="w-full max-w-7xl mx-auto mt-6 px-4">
            <Card className="shadow-xl rounded-2xl border border-gray-200">
                <CardBody className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-gray-800">Job Card Stages Management</h2>
                    </div>

                    {error && (
                        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">
                            {error}
                        </div>
                    )}

                    {/* Form Section */}
                    <Card className="mb-8 bg-white border border-gray-200 rounded-2xl shadow-md hover:shadow-lg transition-shadow">
                        <CardBody className="p-8">
                            <div className="flex items-center justify-between mb-8">
                                <div>
                                    <h3 className="text-2xl font-bold text-gray-900 mb-1">
                                        {editingId ? '✏️ Edit Stage' : '➕ Add New Stage'}
                                    </h3>
                                    <p className="text-sm text-gray-500">
                                        {editingId ? 'Update the stage details below' : 'Create a new job card stage for your workflow'}
                                    </p>
                                </div>
                                {editingId && (
                                    <Button
                                        type="button"
                                        onClick={cancelForm}
                                        className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 shadow-sm transition-all"
                                    >
                                        <FaTimes /> Cancel
                                    </Button>
                                )}
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-8">
                                {/* Basic Information Section */}
                                <div>
                                    <div className="mb-4">
                                        <Typography className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Basic Information</Typography>
                                        <div className="mt-2 h-0.5 w-16 bg-gradient-to-r from-blue-500 to-transparent rounded-full"></div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">Code *</label>
                                            <Input
                                                type="text"
                                                value={form.code}
                                                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                                                className="uppercase font-semibold text-center"
                                                style={{ textTransform: 'uppercase' }}
                                                placeholder="e.g., PENDING"
                                                required
                                            />
                                            <p className="text-xs text-gray-400 mt-1">Unique stage identifier</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">Name *</label>
                                            <Input
                                                type="text"
                                                value={form.name}
                                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                                placeholder="e.g., Pending Review"
                                                required
                                            />
                                            <p className="text-xs text-gray-400 mt-1">Display name for the stage</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Color & Appearance Section */}
                                <div>
                                    <div className="mb-4">
                                        <Typography className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Appearance</Typography>
                                        <div className="mt-2 h-0.5 w-16 bg-gradient-to-r from-purple-500 to-transparent rounded-full"></div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-3">Color Preview</label>
                                            <div className="flex items-center gap-4">
                                                <input
                                                    type="color"
                                                    value={form.color}
                                                    onChange={(e) => setForm({ ...form, color: e.target.value.toUpperCase() })}
                                                    className="w-16 h-16 border-2 border-gray-300 rounded-xl cursor-pointer shadow-sm hover:border-blue-500 transition-colors"
                                                    required
                                                />
                                                <div className="text-center">
                                                    <p className="text-xs text-gray-500 mb-1">Current</p>
                                                    <p className="text-sm font-mono font-bold text-gray-700">{form.color}</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">Hex Color Code *</label>
                                            <Input
                                                type="text"
                                                value={form.color}
                                                onChange={(e) => setForm({ ...form, color: e.target.value.toUpperCase() })}
                                                className="uppercase font-mono text-center"
                                                placeholder="#3B82F6"
                                                required
                                            />
                                            <p className="text-xs text-gray-400 mt-1">Enter hex code (e.g., #FF5733, #3B82F6)</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Configuration Section */}
                                <div>
                                    <div className="mb-4">
                                        <Typography className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Configuration</Typography>
                                        <div className="mt-2 h-0.5 w-16 bg-gradient-to-r from-orange-500 to-transparent rounded-full"></div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">Sort Order</label>
                                            <Input
                                                type="number"
                                                value={form.sort_order}
                                                onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                                                className="text-center"
                                                placeholder="0"
                                            />
                                            <p className="text-xs text-gray-400 mt-1">Display order (numeric)</p>
                                        </div>
                                        <div className="flex items-end">
                                            <label className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200 flex-1 cursor-pointer hover:bg-blue-100 transition-colors">
                                                <div className="flex items-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={form.is_active}
                                                        onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                                                        className="w-5 h-5 cursor-pointer"
                                                    />
                                                </div>
                                                <span className="text-sm font-semibold text-gray-700">Active Stage</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                {/* Stage Behavior Section */}
                                <div>
                                    <div className="mb-4">
                                        <Typography className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Stage Behavior</Typography>
                                        <div className="mt-2 h-0.5 w-16 bg-gradient-to-r from-red-500 to-transparent rounded-full"></div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                        <label className="flex items-center gap-3 p-4 bg-orange-50 rounded-lg border border-orange-200 cursor-pointer hover:bg-orange-100 transition-colors">
                                            <input
                                                type="checkbox"
                                                checked={form.invoice}
                                                onChange={(e) => setForm({ ...form, invoice: e.target.checked })}
                                                className="w-5 h-5 cursor-pointer"
                                            />
                                            <span className="text-sm font-semibold text-gray-700">Invoice Stage</span>
                                        </label>
                                        <label className="flex items-center gap-3 p-4 bg-yellow-50 rounded-lg border border-yellow-200 cursor-pointer hover:bg-yellow-100 transition-colors">
                                            <input
                                                type="checkbox"
                                                checked={form.to_invoice}
                                                onChange={(e) => setForm({ ...form, to_invoice: e.target.checked })}
                                                className="w-5 h-5 cursor-pointer"
                                            />
                                            <span className="text-sm font-semibold text-gray-700">To Invoice</span>
                                        </label>
                                        <label className="flex items-center gap-3 p-4 bg-purple-50 rounded-lg border border-purple-200 cursor-pointer hover:bg-purple-100 transition-colors">
                                            <input
                                                type="checkbox"
                                                checked={form.needs_amount}
                                                onChange={(e) => setForm({ ...form, needs_amount: e.target.checked })}
                                                className="w-5 h-5 cursor-pointer"
                                            />
                                            <div className="flex flex-col">
                                                <span className="text-sm font-semibold text-gray-700">Needs Amount</span>
                                                <span className="text-xs text-gray-500">Requires total_cost &gt; 0</span>
                                            </div>
                                        </label>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <label className="flex items-center gap-3 p-4 bg-red-50 rounded-lg border border-red-200 cursor-pointer hover:bg-red-100 transition-colors">
                                            <input
                                                type="checkbox"
                                                checked={form.locked}
                                                onChange={(e) => setForm({ ...form, locked: e.target.checked })}
                                                className="w-5 h-5 cursor-pointer"
                                            />
                                            <span className="text-sm font-semibold text-gray-700">Locked Stage</span>
                                        </label>
                                        <label className="flex items-center gap-3 p-4 bg-red-50 rounded-lg border border-red-200 cursor-pointer hover:bg-red-100 transition-colors">
                                            <input
                                                type="checkbox"
                                                checked={form.cancel}
                                                onChange={(e) => setForm({ ...form, cancel: e.target.checked })}
                                                className="w-5 h-5 cursor-pointer"
                                            />
                                            <span className="text-sm font-semibold text-gray-700">Cancel Stage</span>
                                        </label>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
                                    {editingId && (
                                        <Button
                                            type="button"
                                            onClick={cancelForm}
                                            className="px-6 py-3 bg-white text-gray-700 border-2 border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition-all"
                                        >
                                            Cancel
                                        </Button>
                                    )}
                                    <Button
                                        type="submit"
                                        className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-semibold shadow-md hover:shadow-lg hover:from-blue-700 hover:to-blue-800 transition-all"
                                    >
                                        <FaPlus /> {editingId ? 'Update Stage' : 'Create Stage'}
                                    </Button>
                                </div>
                            </form>
                        </CardBody>
                    </Card>

                    {/* Table Section */}
                    <Card className="border border-gray-200 rounded-xl">
                        <CardBody className="p-0">
                            <div className="bg-gradient-to-r from-gray-100 to-gray-50 px-6 py-4 border-b border-gray-200">
                                <h3 className="text-lg font-bold text-gray-800">Existing Stages</h3>
                            </div>

                            {loading ? (
                                <div className="p-8 text-center">
                                    <Typography className="text-gray-600">Loading stages...</Typography>
                                </div>
                            ) : (
                                <>
                                    {/* Desktop Table View */}
                                    <div className="hidden md:block overflow-x-auto">
                                        <table className="w-full table-auto border-collapse">
                                            <thead>
                                                <tr className="bg-gray-50 border-b-2 border-gray-200">
                                                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Color</th>
                                                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Code</th>
                                                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Name</th>
                                                    <th className="px-6 py-4 text-center text-sm font-bold text-gray-700 uppercase tracking-wider">Sort Order</th>
                                                    <th className="px-6 py-4 text-center text-sm font-bold text-gray-700 uppercase tracking-wider">Invoice</th>
                                                    <th className="px-6 py-4 text-center text-sm font-bold text-gray-700 uppercase tracking-wider">To Invoice</th>
                                                    <th className="px-6 py-4 text-center text-sm font-bold text-gray-700 uppercase tracking-wider">Needs Amount</th>
                                                    <th className="px-6 py-4 text-center text-sm font-bold text-gray-700 uppercase tracking-wider">Locked</th>
                                                    <th className="px-6 py-4 text-center text-sm font-bold text-gray-700 uppercase tracking-wider">Cancel</th>
                                                    <th className="px-6 py-4 text-center text-sm font-bold text-gray-700 uppercase tracking-wider">Status</th>
                                                    <th className="px-6 py-4 text-center text-sm font-bold text-gray-700 uppercase tracking-wider">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {items.map((item) => (
                                                    <tr key={item.id} className="hover:bg-blue-50 transition-colors">
                                                        <td className="px-6 py-4">
                                                            <div className="flex items-center gap-3">
                                                                <div
                                                                    style={{ backgroundColor: item.color }}
                                                                    className="w-10 h-10 rounded-lg border-2 border-gray-300 shadow-sm"
                                                                ></div>
                                                                <Typography className="text-xs font-mono font-semibold text-gray-600">
                                                                    {item.color}
                                                                </Typography>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <Typography className="uppercase text-sm font-bold text-gray-900">
                                                                {item.code}
                                                            </Typography>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <Typography className="text-sm text-gray-800 font-medium">
                                                                {item.name}
                                                            </Typography>
                                                        </td>
                                                        <td className="px-6 py-4 text-center">
                                                            <span className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-gray-100 text-gray-800 text-sm font-semibold">
                                                                {item.sort_order}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 text-center">
                                                            {item.invoice ? (
                                                                <span className="inline-flex items-center px-3 py-1 rounded-full bg-orange-100 text-orange-800 text-sm font-semibold">
                                                                    Yes
                                                                </span>
                                                            ) : (
                                                                <span className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-sm font-semibold">
                                                                    No
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="px-6 py-4 text-center">
                                                            {item.to_invoice ? (
                                                                <span className="inline-flex items-center px-3 py-1 rounded-full bg-yellow-100 text-yellow-800 text-sm font-semibold">
                                                                    Yes
                                                                </span>
                                                            ) : (
                                                                <span className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-sm font-semibold">
                                                                    No
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="px-6 py-4 text-center">
                                                            {item.needs_amount ? (
                                                                <span className="inline-flex items-center px-3 py-1 rounded-full bg-purple-100 text-purple-800 text-sm font-semibold">
                                                                    Yes
                                                                </span>
                                                            ) : (
                                                                <span className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-sm font-semibold">
                                                                    No
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="px-6 py-4 text-center">
                                                            {item.locked ? (
                                                                <span className="inline-flex items-center px-3 py-1 rounded-full bg-red-100 text-red-800 text-sm font-semibold">
                                                                    Yes
                                                                </span>
                                                            ) : (
                                                                <span className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-sm font-semibold">
                                                                    No
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="px-6 py-4 text-center">
                                                            {item.cancel ? (
                                                                <span className="inline-flex items-center px-3 py-1 rounded-full bg-red-100 text-red-800 text-sm font-semibold">
                                                                    Yes
                                                                </span>
                                                            ) : (
                                                                <span className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-sm font-semibold">
                                                                    No
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="px-6 py-4 text-center">
                                                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${item.is_active
                                                                ? 'bg-green-100 text-green-800'
                                                                : 'bg-gray-100 text-gray-600'
                                                                }`}>
                                                                {item.is_active ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <div className="flex items-center justify-center gap-2">
                                                                <Button
                                                                    onClick={() => startEdit(item)}
                                                                    className="bg-blue-600 hover:bg-blue-700 text-white p-2 shadow-md hover:shadow-lg transition-all"
                                                                    size="sm"
                                                                    disabled={editingId === item.id}
                                                                >
                                                                    <FaPenAlt className="w-3 h-3" />
                                                                </Button>
                                                                <Button
                                                                    onClick={() => toggleActive(item)}
                                                                    className={`${item.is_active
                                                                        ? 'bg-gray-600 hover:bg-gray-700'
                                                                        : 'bg-green-600 hover:bg-green-700'
                                                                        } text-white p-2 shadow-md hover:shadow-lg transition-all`}
                                                                    size="sm"
                                                                >
                                                                    {item.is_active ? (
                                                                        <FaToggleOff className="w-4 h-4" />
                                                                    ) : (
                                                                        <FaToggleOn className="w-4 h-4" />
                                                                    )}
                                                                </Button>
                                                                <Button
                                                                    onClick={() => deleteItem(item.id)}
                                                                    className="bg-red-600 hover:bg-red-700 text-white p-2 shadow-md hover:shadow-lg transition-all"
                                                                    size="sm"
                                                                    disabled={editingId === item.id}
                                                                >
                                                                    <FaTrash className="w-3 h-3" />
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Mobile Card View */}
                                    <div className="md:hidden space-y-4 p-4">
                                        {items.map((item) => (
                                            <Card key={item.id} className="border border-gray-200 shadow-md">
                                                <CardBody className="p-4">
                                                    <div className="flex items-start justify-between mb-4">
                                                        <div className="flex items-center gap-3">
                                                            <div
                                                                style={{ backgroundColor: item.color }}
                                                                className="w-12 h-12 rounded-lg border-2 border-gray-300 shadow-sm"
                                                            ></div>
                                                            <div>
                                                                <Typography className="uppercase text-base font-bold text-gray-900">
                                                                    {item.code}
                                                                </Typography>
                                                                <Typography className="text-xs font-mono text-gray-600">
                                                                    {item.color}
                                                                </Typography>
                                                            </div>
                                                        </div>
                                                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${item.is_active
                                                            ? 'bg-green-100 text-green-800'
                                                            : 'bg-gray-100 text-gray-600'
                                                            }`}>
                                                            {item.is_active ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </div>

                                                    <div className="space-y-2 mb-4">
                                                        <div className="flex justify-between">
                                                            <Typography className="text-sm text-gray-600 font-medium">Name:</Typography>
                                                            <Typography className="text-sm text-gray-900 font-semibold">
                                                                {item.name}
                                                            </Typography>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <Typography className="text-sm text-gray-600 font-medium">Sort Order:</Typography>
                                                            <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-800 text-xs font-semibold">
                                                                {item.sort_order}
                                                            </span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <Typography className="text-sm text-gray-600 font-medium">Invoice:</Typography>
                                                            {item.invoice ? (
                                                                <span className="px-3 py-1 rounded-full bg-orange-100 text-orange-800 text-xs font-semibold">
                                                                    Yes
                                                                </span>
                                                            ) : (
                                                                <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">
                                                                    No
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <Typography className="text-sm text-gray-600 font-medium">To Invoice:</Typography>
                                                            {item.to_invoice ? (
                                                                <span className="px-3 py-1 rounded-full bg-yellow-100 text-yellow-800 text-xs font-semibold">
                                                                    Yes
                                                                </span>
                                                            ) : (
                                                                <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">
                                                                    No
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <Typography className="text-sm text-gray-600 font-medium">Locked:</Typography>
                                                            {item.locked ? (
                                                                <span className="px-3 py-1 rounded-full bg-red-100 text-red-800 text-xs font-semibold">
                                                                    Yes
                                                                </span>
                                                            ) : (
                                                                <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">
                                                                    No
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <Typography className="text-sm text-gray-600 font-medium">Cancel:</Typography>
                                                            {item.cancel ? (
                                                                <span className="px-3 py-1 rounded-full bg-red-100 text-red-800 text-xs font-semibold">
                                                                    Yes
                                                                </span>
                                                            ) : (
                                                                <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">
                                                                    No
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-200">
                                                        <Button
                                                            onClick={() => startEdit(item)}
                                                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 shadow-md flex items-center gap-2"
                                                            size="sm"
                                                            disabled={editingId === item.id}
                                                        >
                                                            <FaPenAlt className="w-3 h-3" />
                                                            <span className="text-xs">Edit</span>
                                                        </Button>
                                                        <Button
                                                            onClick={() => toggleActive(item)}
                                                            className={`${item.is_active
                                                                ? 'bg-gray-600 hover:bg-gray-700'
                                                                : 'bg-green-600 hover:bg-green-700'
                                                                } text-white px-3 py-2 shadow-md`}
                                                            size="sm"
                                                        >
                                                            {item.is_active ? (
                                                                <FaToggleOff className="w-4 h-4" />
                                                            ) : (
                                                                <FaToggleOn className="w-4 h-4" />
                                                            )}
                                                        </Button>
                                                        <Button
                                                            onClick={() => deleteItem(item.id)}
                                                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 shadow-md"
                                                            size="sm"
                                                            disabled={editingId === item.id}
                                                        >
                                                            <FaTrash className="w-3 h-3" />
                                                        </Button>
                                                    </div>
                                                </CardBody>
                                            </Card>
                                        ))}
                                    </div>
                                </>
                            )}
                        </CardBody>
                    </Card>
                </CardBody>
            </Card>
        </div>
    );
};

export default JobCardStagesManagement;
